import { useEffect, useRef } from 'react';

interface TabDetectorConfig {
  /**
   * Función que se ejecutará cuando se detecte un cambio de pestaña. Es
   * obligatoria: un detector sin nada que notificar no tiene razón de existir.
   */
  onTabSwitch: () => void;
  /**
   * Indica si el detector está activo. Es obligatorio a propósito: un valor
   * por defecto silencioso puede dejar el hook armado —o muerto— sin que
   * ningún llamador lo decida.
   */
  isActive: boolean;
}

/**
 * Hook personalizado que detecta cuando un usuario cambia de pestaña.
 *
 * La señal es `visibilitychange` de `document` junto con `document.hidden`, no
 * el `blur` de `window`: este último se dispara con cualquier pérdida de foco
 * de la ventana (una notificación de Teams, un alt-tab, abrir DevTools) sin que
 * el usuario haya salido de la pestaña.
 *
 * Notifica **una sola vez** mientras el detector siga activo.
 *
 * @example
 * ```tsx
 * useSwitchTabDetector({
 *   onTabSwitch: failTest,
 *   isActive: isTestInProgress,
 * });
 * ```
 */
const useSwitchTabDetector = ({ onTabSwitch, isActive }: TabDetectorConfig) => {
  /**
   * El callback vive en una ref porque llega como una flecha recreada en cada
   * render del consumidor. Si formara parte de las dependencias del efecto, el
   * listener se volvería a suscribir en cada render y `hasNotifiedTabSwitch` se
   * reiniciaría, permitiendo múltiples notificaciones por el mismo examen.
   */
  const onTabSwitchRef = useRef(onTabSwitch);
  onTabSwitchRef.current = onTabSwitch;

  const hasNotifiedTabSwitch = useRef(false);

  useEffect(() => {
    if (!isActive) {
      return;
    }

    const handleVisibilityChange = () => {
      const isTabStillVisible = !document.hidden;

      if (isTabStillVisible || hasNotifiedTabSwitch.current) {
        return;
      }

      hasNotifiedTabSwitch.current = true;

      onTabSwitchRef.current();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isActive]);
};

export default useSwitchTabDetector;
